import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";
import FormContainer from "@ats/src/components/forms/FormContainer";

import { useModalContext } from "@shared/context/ModalContext";
import { useToastContext } from "@shared/context/ToastContext";
import { useOrganizationAiCreditBalance } from "@shared/queryHooks/useOrganizationAiCreditBalance";
import { useBulkGenerateAiSummaries } from "@shared/queryHooks/useBulkGenerateAiSummaries";
import { validateBulkGenerateAiSummaries } from "@shared/lib/validateWithYup";
import { trackEvent } from "@shared/lib/posthog";

interface Props {
  jobId: number;
  hiringStageId: number;
  onCancel: () => void;
  idsArray: Array<number>;
  idsArrayType: string;
  roleFit: Array<string>;
  knockoutPass: string;
  knockoutFail: string;
  knockoutNoReply: string;
  source: Array<string>;
  hasResume: boolean | null;
  createdVia: Array<string>;
  sort: string;
  sortDirection: string;
  candidatesCount: number;
  processableCount: number;
  isProcessableCountExact: boolean;
  resetList: () => void;
}

function BulkGenerateAiSummariesConfirmModal({
  jobId,
  hiringStageId,
  onCancel,
  idsArray,
  idsArrayType,
  roleFit,
  knockoutPass,
  knockoutFail,
  knockoutNoReply,
  source,
  hasResume,
  createdVia,
  sort,
  sortDirection,
  candidatesCount,
  processableCount,
  isProcessableCountExact,
  resetList,
}: Props) {
  const { dismissModalWithAnimation } = useModalContext();
  const addToast = useToastContext();
  const { mutate: bulkGenerate, isLoading } = useBulkGenerateAiSummaries();

  const [errors, setErrors] = React.useState([]);

  const { data, isError } = useOrganizationAiCreditBalance();
  const available = isError ? 0 : data?.totalCreditsRemaining || 0;
  // Only processable candidates (no existing "current" summary) consume credits.
  const shortfall = Math.max(0, processableCount - available);

  const handleOnCancel = () => {
    dismissModalWithAnimation(() => onCancel);
  };

  const handleOnConfirm = async (event) => {
    event.preventDefault();
    const [isValid, validationErrors] = await validateBulkGenerateAiSummaries({
      availableCredits: available,
    });
    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    bulkGenerate(
      {
        jobId,
        hiringStageId,
        excludedJobApplicationIds: idsArrayType === "excluded" ? idsArray : [],
        includedJobApplicationIds: idsArrayType === "included" ? idsArray : [],
        roleFit,
        knockoutPass,
        knockoutFail,
        knockoutNoReply,
        source,
        hasResume,
        createdVia,
        sort,
        sortDirection,
        rescoreRequested: false,
      },
      {
        onSuccess: (data: { queuedCount: number; skippedCount: number; anyTextractPending: boolean }) => {
          trackEvent("bulk_generate_ai_summaries_completed", { job_id: jobId, hiring_stage_id: hiringStageId, candidates_count: candidatesCount });
          const parts = [];
          if (data.queuedCount > 0) {
            parts.push(`Queued ${data.queuedCount} review${data.queuedCount === 1 ? "" : "s"}`);
          }
          if (data.skippedCount > 0) {
            parts.push(`${data.skippedCount} skipped (no resume, already processing, or not enough credits)`);
          }
          if (data.anyTextractPending) {
            parts.push("some resumes are still processing");
          }
          const title = parts.length > 0 ? parts.join(". ") : "No reviews to generate";
          addToast({ title, kind: "success" });
          resetList();
          dismissModalWithAnimation(() => onCancel);
        },
        onError: (error: any) => {
          addToast({
            title: error?.data?.errors?.general?.[0] || "Failed to queue reviews",
            kind: "warning",
            delay: 10000,
          });
        },
      },
    );
  };

  const shortfallText = (
    <>
      You are short <b>{shortfall}</b> credit{shortfall === 1 ? "" : "s"}. The first {available}{" "}
      candidate{available === 1 ? "" : "s"} will get reviews generated; the rest will be skipped.
    </>
  );

  const instructions =
    candidatesCount === 0 ? (
      <>
        <span>No candidates selected.</span> Use the checkboxes next to the candidate names to select
        the candidates to generate reviews for. To <b>select all candidates</b> within this hiring
        stage, use the checkbox next to the stage name.
      </>
    ) : processableCount === 0 ? (
      <>
        <span>Nothing to generate.</span> Every selected candidate already has a review, so none
        will be reprocessed.
      </>
    ) : isProcessableCountExact ? (
      <>
        Generate Plato reviews for <b>{processableCount}</b> candidate
        {processableCount === 1 ? "" : "s"}.{" "}
        {shortfall > 0 ? (
          shortfallText
        ) : (
          <>
            This will use <b>{processableCount}</b> credit{processableCount === 1 ? "" : "s"} from
            your balance of <b>{available}</b> available.
          </>
        )}
      </>
    ) : (
      <>
        Generate Plato reviews for up to <b>{processableCount}</b> candidate
        {processableCount === 1 ? "" : "s"}
        {shortfall > 0 ? (
          <>. {shortfallText}</>
        ) : (
          <>
            , using up to <b>{processableCount}</b> credit{processableCount === 1 ? "" : "s"} from
            your balance of <b>{available}</b> available.
          </>
        )}
      </>
    );

  const modalButtons = (
    <Styled.ButtonContainer>
      <Button
        className="submit-button"
        type="submit"
        size="medium"
        onClick={handleOnConfirm}
        loading={isLoading}
        disabled={isLoading || processableCount === 0}
        aria-label="Generate reviews"
      >
        Generate reviews
      </Button>
      <Button
        styleType="secondary"
        onClick={(event) => {
          if (event) event.preventDefault();
          handleOnCancel();
        }}
      >
        Cancel
      </Button>
    </Styled.ButtonContainer>
  );

  return (
    <CenterModal
      headerTitleText="Review candidates"
      onCancel={handleOnCancel}
    >
      <Styled.Instructions>{instructions}</Styled.Instructions>
      <FormContainer errors={errors} buttons={modalButtons} onSubmit={handleOnConfirm}>
        {!isProcessableCountExact && processableCount > 0 && (
          <Styled.Caveat>
            This is a Select All and not all candidates are loaded, so this may be an overestimate.
            Candidates who already have a review won't be reprocessed.
          </Styled.Caveat>
        )}
        <Styled.Callout>
          <Icon name="file-text" />
          <span>
            Candidates you selected that already have a review were excluded from this count. Candidates may also be skipped if they have no resume, their resume is still processing, or they're already part of another bulk operation.
          </span>
        </Styled.Callout>
      </FormContainer>
    </CenterModal>
  );
}

export default BulkGenerateAiSummariesConfirmModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Instructions = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: BulkGenerateAiSummariesConfirmModal_Instructions;
    ${[t.mb(5)]}

    span {
      font-weight: bold;
      color: ${t.dark ? t.color.gray[200] : t.color.black};
    }
  `;
});

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: BulkGenerateAiSummariesConfirmModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}

      &:last-of-type {
        ${t.mr(0)}
      }
    }
  `;
});

Styled.Caveat = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: BulkGenerateAiSummariesConfirmModal_Caveat;
    ${[t.p(3), t.rounded.md, t.text.sm]}
    background: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
    color: ${t.dark ? t.color.gray[300] : t.color.gray[700]};

    b {
      color: ${t.dark ? t.color.gray[100] : t.color.black};
    }
  `;
});

Styled.Callout = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: BulkGenerateAiSummariesConfirmModal_Callout;
    ${[t.mt(4), t.p(3), t.rounded.md]}
    display: flex;
    gap: 0.625rem;
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
    line-height: 1.5;
    ${t.text.sm}

    svg {
      flex-shrink: 0;
      margin-top: 0.0625rem;
      width: 1rem;
      height: 1rem;
      color: ${t.dark ? t.color.gray[500] : t.color.gray[400]};
    }
  `;
});